import React, { useState, useEffect } from "react";
import DivTag from "../Components/DivComponent/DivTag";
import ReportHeading from "../Components/ReportHeadings/ReportHeadings";
import { motion } from "framer-motion";
import Seo from "../../components/SeoComponent/Seo";
import { ToastContainer } from "react-toastify";
import Loader from "../../components/Loader/Loader";
import { FaTimes, FaInfoCircle, FaEye } from "react-icons/fa";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { MdDownload } from "react-icons/md";
import { DocumentPreviewService } from "../../services/apiService";
import {
  SignUpReportService,
  UpdateUserStatus,
  UserDigitalKycStatus,
  GetUserDocuments,
  UserKycStatus,
  UpdateDoctStatus,
} from "../../services/SignUpReport/SignUpReport";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FileArchive } from "lucide-react";

const SignUpReport = () => {
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [localComment, setLocalComment] = useState("");
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [comment, setComment] = useState("");
  const [fileUrl, setFileUrl] = useState(null);
  const [referredBy, setReferredBy] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpendoc, setIsModalOpenDoc] = useState(false);
  const [selectedUserDocs, setSelectedUserDocs] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
  });

  const getFormattedDate = (date) => date.toISOString().split("T")[0];
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const [filters, setFilters] = useState({
    total_data: 10,
    from_date: getFormattedDate(yesterday),
    to_date: getFormattedDate(today),
    status: "",
    api_id: "",
    mobile: "",
    email: "",
  });

  //  Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    handleSearch(true); //  pass "true" for silent load
  }, []);

  // 🧠 Modify handleSearch to accept a parameter
  const handleSearch = async (isInitial = false) => {
    setIsLoading(true);
    const finalFilters = { ...filters, total_data: filters.total_data || 10 };

    try {
      const response = await SignUpReportService(1, finalFilters);
      const isSuccess =
        response?.status === 200 ||
        response?.status?.code === 200 ||
        response?.status?.code === 201 ||
        response?.success === true ||
        response?.data?.success === true;

      if (isSuccess) {
        //  Only show toast when not initial load
        if (!isInitial) {
          showSuccessToast(response?.message || "Report fetched successfully!");
        }

        setReportData(response?.data?.data || response?.data || []);
        setAllEmployees(response?.referred_by_options);
        setPagination(response?.data?.pagination || response?.pagination || {});
      } else {
        const message =
          response?.message ||
          response?.data?.message ||
          "Failed to fetch report.";
        showErrorToast(message);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "An unexpected error occurred while fetching the report.";
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  //  Handle status change — open confirmation modal
  const handleChangeStatus = (user, status, documentId) => {
    // 🔹 Temporarily update the status value in UI
    user.status.value = status;
    setReferredBy(user?.referred_by);
    // 🔹 Open modal for confirmation
    setSelectedUser({ ...user, document_id: documentId });
    setSelectedStatus(status);
    setIsModalOpen(true);
  };

  // Update user Status
  const handleConfirmUpdate = async () => {
    if (!selectedUser || !selectedStatus) return;

    setIsLoading(true);
    try {
      const response = await UpdateUserStatus({
        user_id: selectedUser.id,
        status: selectedStatus,
        comment,
        referred_by: referredBy
      });

      const code =
        response?.status?.code ||
        response?.code ||
        response?.status ||
        response?.data?.status?.code;

      const errorObj =
        response?.errors || response?.data?.errors || response?.status?.errors;

      const message =
        response?.status?.message ||
        response?.message ||
        response?.data?.status?.message;

      // ✅ Handle 422 Validation Error
      if (code === 422) {
        if (errorObj && typeof errorObj === "object") {
          // Extract all error messages from the errors object
          const errorMessages = Object.values(errorObj)
            .flat() // Flatten arrays
            .join(", "); // Join multiple errors
          showErrorToast(errorMessages || message || "Validation failed");
        } else {
          showErrorToast(message || "Validation failed");
        }
        return;
      }

      // ✅ Handle Success
      if (code === 200) {
        showSuccessToast(message || "Status updated successfully!");
        return;
      }

      // ✅ Handle other errors
      showErrorToast(message || "Unexpected response from server");
    } catch (error) {
      // ✅ Handle Axios error response
      const errorObj = error.response?.data?.errors;

      if (errorObj && typeof errorObj === "object") {
        const errorMessages = Object.values(errorObj).flat().join(", ");
        showErrorToast(errorMessages);
      } else {
        const errMsg =
          error.response?.data?.status?.message ||
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred.";
        showErrorToast(errMsg);
      }
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  // 🔁 Pagination Change Handler
  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > pagination.last_page) return;
    setIsLoading(true);
    try {
      const response = await SignUpReportService(newPage, filters);
      if (response?.success || response?.status.code === 200) {
        setReportData(response?.data || response?.data?.data || []);
        setPagination(response?.data?.pagination || response?.pagination || {});
      } else {
        showErrorToast("Failed to fetch page data");
      }
    } catch (error) {
      showErrorToast("Error fetching next page");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Fetch user documents and open modal ---
  const handleViewDocuments = async (user) => {
    setIsLoading(true);
    setSelectedUser(user);

    try {
      const response = await GetUserDocuments(user.id);
      const statusCode = response?.status?.code ?? response?.status;
      const isSuccess = statusCode === 200;
      // console.log("resDoct", response);

      if (isSuccess) {
        const docs = Array.isArray(response?.data) ? response.data : [];
        // console.log("Docs",docs);
        if (docs.length > 0) {
          setSelectedUserDocs(docs);
          setSelectedDocId(docs[0]?.document_id ?? null);
          setLocalComment("");
          setIsModalOpenDoc(true);
        } else {
          setIsModalOpenDoc(true);
          showErrorToast("No documents found for this user.");
        }
      } else {
        showErrorToast(
          response?.status?.message || "Failed to load documents.",
        );
      }
    } catch (error) {
      showErrorToast("An error occurred while fetching documents.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Helper function to extract document name from remarks ---
  const getDocumentName = (doc) => {
    // 1 Always prefer backend document name
    if (doc?.document?.name) {
      return doc.document.name;
    }

    // 2️⃣ Fallback (safety only)
    return `Document ${doc.document_id}`;
  };

  const [previewImage, setPreviewImage] = useState(null);

  const handleImageClick = (imageUrl) => setPreviewImage(imageUrl);
  const closePreview = () => setPreviewImage(null);

  const handleDownloadDocument = () => {
    if (!fileUrl) {
      showErrorToast("No file to download");
      return;
    }

    const byteString = atob(fileUrl.split(",")[1]);
    const mimeString = fileUrl.split(",")[0].split(":")[1].split(";")[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    const url = URL.createObjectURL(blob);

    const docName = getDocumentName(
      selectedUserDocs.find((doc) => doc.document_id == selectedDocId),
    );

    const link = document.createElement("a");
    link.href = url;

    // auto extension detection
    let ext = mimeString.includes("pdf") ? "pdf" : "jpg";
    link.download = `${docName}.${ext}`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const exportToExcel = (data) => {
    // Convert JSON to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Save file
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, "Report.xlsx");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2 }}
    >
      <Seo />
      <DivTag className="mt-8 bg-white rounded-lg shadow-md">
        <ReportHeading name="SignUp User Report" />
        {isLoading && <Loader />}

        {/* 🔍 Filter Section */}
        <div className="px-3 py-1 bg-white shadow-md rounded-lg border border-gray-200 mb-1 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {/* 📅 From Date */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">
                From Date
              </label>
              <input
                type="date"
                name="from_date"
                value={filters.from_date}
                onChange={handleChange}
                className="border border-gray-300 rounded-md p-1 text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              />
            </div>

            {/* 📅 To Date */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">
                To Date
              </label>
              <input
                type="date"
                name="to_date"
                value={filters.to_date}
                onChange={handleChange}
                className="border border-gray-300 rounded-md p-1 text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              />
            </div>

            {/* ⚙️ Status */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleChange}
                className="border border-gray-300 rounded-md p-1 text-sm bg-white focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              >
                <option value="">All</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REKYC">ReKYC</option>
                <option value="REJECTED">Rejected</option>
                <option value="HOLD">Hold</option>
              </select>
            </div>

            {/* 🆔 API ID */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">
                API ID
              </label>
              <input
                type="text"
                name="api_id"
                value={filters.api_id}
                onChange={handleChange}
                placeholder="Enter API ID"
                className="border border-gray-300 rounded-md p-1 text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              />
            </div>

            {/* 📱 Mobile */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">
                Mobile
              </label>
              <input
                type="text"
                name="mobile"
                value={filters.mobile}
                onChange={handleChange}
                placeholder="Enter Mobile"
                className="border border-gray-300 rounded-md p-1 text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              />
            </div>

            {/* 📧 Email */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">
                Email
              </label>
              <input
                type="text"
                name="email"
                value={filters.email}
                onChange={handleChange}
                placeholder="Enter Email"
                className="border border-gray-300 rounded-md p-1 text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              />
            </div>

            {/* 📊 Show Entries */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-600 mb-1">
                Show Entries
              </label>
              <select
                name="total_data"
                value={filters.total_data}
                onChange={handleChange}
                className="border border-gray-300 rounded-md p-1 text-sm bg-white focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          {/* 🚀 Search Button */}
          <div className="flex justify-center mt-2 mb-2">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className={`px-3 py-1 text-sm font-semibold rounded-md transition-all duration-200 ${
                isLoading
                  ? "bg-gray-400 cursor-not-allowed text-white"
                  : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md"
              }`}
            >
              {isLoading ? "Loading..." : "Search"}
            </button>
          </div>
        </div>

        {reportData?.length > 0 && (
          <button
            onClick={() => exportToExcel(reportData)}
            className="bg-green-50 py-2 my-4 ml-4 px-4 flex items-center gap-3 justify-center rounded-lg text-green-800"
          >
            <FileArchive />
            Download report
          </button>
        )}

        {/* 📊 Table Section */}
        <div className="overflow-x-auto p-1">
          <table className="min-w-full border text-sm">
            <thead className="bg-gray-100">
              <tr className="">
                {[
                  "SNo",
                  "API ID",
                  "Mobile",
                  "Email",
                  "Referred_By",
                  "Business_Name",
                  "Signatory_Name",
                  "GST",
                  "Business_PAN",
                  "Created_At",
                  "Updated_At",
                  "Document",
                  "Action",
                ].map((head, i) => (
                  <th key={i} className="border p-1 text-left">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* 🌀 Loader Row */}
              {isLoading && (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              )}

              {/* 🧩 Data Rows */}
              {!isLoading && reportData?.length > 0
                ? reportData.map((user, index) => (
                    <tr key={user.id} className="border hover:bg-gray-50">
                      <td className="border p-1">
                        {(pagination.current_page - 1) * pagination.per_page +
                          index +
                          1}
                      </td>
                      <td className="border p-1">{user.user_id}</td>
                      <td className="border p-1">{user.mobile_number}</td>
                      <td className="border p-1 text-xs">
                        {user.email_address}
                      </td>
                      <td className="border p-1 text-xs">{user.referred_by}</td>
                      <td className="border p-1">
                        {user.company_details?.company_name || "—"}
                        <FaInfoCircle
                          className="inline ml-2 text-blue-500 cursor-pointer text-xl"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowCompanyModal(true);
                          }}
                        />
                      </td>
                      <td className="border p-1 text-xs">
                        {user.name || "—"}
                        <FaInfoCircle
                          className="inline ml-2 text-blue-500 cursor-pointer text-xl"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserModal(true);
                          }}
                        />
                      </td>
                      <td className="border p-1">
                        {user.company_details?.company_gst_no || "—"}
                      </td>
                      <td className="border p-1">
                        {user.company_details?.company_pan || "—"}
                      </td>
                      <td className="border p-1 text-xs">
                        {user.created_at
                          ? new Date(user.created_at).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "—"}
                      </td>
                      <td className="border p-1 text-xs">
                        {user.updated_at
                          ? new Date(user.updated_at).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "—"}
                      </td>

                      <td className="border p-1 text-center">
                        <button
                          onClick={() => handleViewDocuments(user)}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          disabled={isLoading}
                          title="View Documents"
                        >
                          {isLoading ? (
                            <span className="text-gray-400 text-sm">...</span>
                          ) : (
                            <FaEye size={28} />
                          )}
                        </button>
                      </td>

                      <td className="border p-1 text-center">
                        <select
                          className={`border text-xs rounded px-1 py-0 font-semibold
                        ${
                          user?.status?.value?.toUpperCase() === "APPROVED"
                            ? "bg-green-100 text-green-700 border-green-400"
                            : user?.status?.value?.toUpperCase() === "REJECTED"
                              ? "bg-red-100 text-red-700 border-red-400"
                              : user?.status?.value?.toUpperCase() === "HOLD"
                                ? "bg-yellow-100 text-yellow-700 border-yellow-400"
                                : user?.status?.value?.toUpperCase() === "REKYC"
                                  ? "bg-blue-100 text-blue-700 border-blue-400"
                                  : "bg-gray-100 text-gray-700 border-gray-300"
                        }`}
                          value={
                            user?.status?.value?.toUpperCase() || "PENDING"
                          }
                          onChange={(e) =>
                            handleChangeStatus(user, e.target.value, 3)
                          }
                        >
                          <option value="PENDING">PENDING</option>
                          <option value="APPROVED">APPROVED</option>
                          <option value="REJECTED">REJECTED</option>
                          <option value="HOLD">HOLD</option>
                          <option value="REKYC">REKYC</option>
                        </select>
                      </td>
                    </tr>
                  ))
                : !isLoading && (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        No records found
                      </td>
                    </tr>
                  )}

              {/*  Company Info Modal */}
              {showCompanyModal && selectedUser && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[9999] backdrop-blur-sm animate-fadeIn">
                  <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 rounded-2xl shadow-2xl p-2 w-[95%] max-w-lg relative border border-indigo-200 transform transition-all duration-300 animate-scaleUp">
                    {/* ✨ Header */}
                    <h2 className="text-xl font-bold mb-4 text-indigo-700 text-center border-b border-indigo-200 pb-2">
                      🏢 Company Details
                    </h2>

                    {/* 🧩 Info Section */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700 text-sm">
                      {[
                        {
                          label: "CIN",
                          value:
                            selectedUser?.company_details?.company_cin || "-",
                        },
                        {
                          label: "Company GST",
                          value:
                            selectedUser?.company_details?.company_gst_no ||
                            "-",
                        },
                        {
                          label: "Company PAN",
                          value:
                            selectedUser?.company_details?.company_pan || "-",
                        },
                        {
                          label: "Company address",
                          value:
                            selectedUser?.company_details?.company_address ||
                            "-",
                        },
                        {
                          label: "Domain",
                          value:
                            selectedUser?.company_details?.company_domain ||
                            "-",
                        },
                        {
                          label: "Business",
                          value:
                            selectedUser?.company_details?.line_of_business ||
                            "-",
                        },
                        {
                          label: "Company Type",
                          value:
                            selectedUser?.company_details?.company_type || "-",
                        },
                        {
                          label: "Company Description",
                          value:
                            selectedUser?.company_details
                              ?.business_description || "-",
                        },
                      ].map(({ label, value }) => (
                        <p key={label}>
                          <span className="font-semibold text-blue-600">
                            {label}:
                          </span>{" "}
                          {typeof value === "object"
                            ? value?.name || JSON.stringify(value)
                            : value || "—"}
                        </p>
                      ))}
                    </div>

                    {/* ✨ Close Icon */}
                    <button
                      onClick={() => setShowCompanyModal(false)}
                      className="absolute top-2 right-3 text-gray-400 hover:text-red-500 text-2xl transition-colors"
                      title="Close"
                    >
                      ×
                    </button>

                    {/* ✨ Footer Button */}
                    <div className="mt-5 flex justify-center">
                      <button
                        onClick={() => setShowCompanyModal(false)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-1.5 rounded-lg text-sm font-medium shadow-md transition-all"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ User Info Modal */}
              {showUserModal && selectedUser && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[9999] backdrop-blur-sm">
                  <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-2xl shadow-2xl p-2 w-[95%] max-w-lg relative border border-blue-200">
                    {/* ✨ Header */}
                    <h2 className="text-xl font-bold mb-2 text-blue-700 text-center border-b border-blue-200 pb-1">
                      Signatory Details
                    </h2>

                    {/* 🧩 Info Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700 text-sm">
                      <p>
                        <span className="font-semibold text-indigo-600">
                          User ID:
                        </span>{" "}
                        {selectedUser.user_id || "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-600">
                          Signatory Name:
                        </span>{" "}
                        {selectedUser.name || "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-600">
                          Mobile:
                        </span>{" "}
                        {selectedUser.mobile_number || "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-600">
                          Aadhar:
                        </span>{" "}
                        {selectedUser.aadhar_number || "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-600">
                          DOB:
                        </span>{" "}
                        {selectedUser.dob || "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-600">
                          User Type:
                        </span>
                        {selectedUser.user_type || "-"}
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-600">
                          Signatory PAN:
                        </span>{" "}
                        {selectedUser.pan_number || "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-indigo-600">
                          Email:
                        </span>{" "}
                        {selectedUser.email_address || "—"}
                      </p>
                      <p className="sm:col-span-2">
                        <span className="font-semibold text-indigo-600">
                          Landmark Address:
                        </span>{" "}
                        {selectedUser.landmark_address || "—"}
                      </p>
                    </div>

                    {/* ✨ Close Button */}
                    <button
                      onClick={() => setShowUserModal(false)}
                      className="absolute top-2 right-3 text-gray-400 hover:text-red-500 text-2xl transition-colors"
                      title="Close"
                    >
                      ×
                    </button>

                    {/* ✨ Footer */}
                    <div className="mt-5 flex justify-center">
                      <button
                        onClick={() => setShowUserModal(false)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-1.5 rounded-lg text-sm font-medium shadow-md transition-all"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </tbody>
          </table>

          {/* 🔄 Pagination Controls */}
          <div className="flex justify-center items-center mt-2">
            <span className="text-sm text-black">
              Page {pagination?.current_page || 1} of{" "}
              {pagination?.last_page || 1}
            </span>

            <div className="space-x-2 ms-2">
              <button
                disabled={!pagination?.prev_page_url}
                onClick={() => handlePageChange(pagination.current_page - 1)}
                className={`px-2 py-0 rounded border border-blue-600 ${
                  pagination?.prev_page_url
                    ? "bg-gray-100 hover:bg-gray-200"
                    : "bg-gray-50 text-gray-400 cursor-not-allowed"
                }`}
              >
                Prev
              </button>

              <button
                disabled={!pagination?.next_page_url}
                onClick={() => handlePageChange(pagination.current_page + 1)}
                className={`px-2 py-0 rounded border  border-blue-600 ${
                  pagination?.next_page_url
                    ? "bg-gray-100 hover:bg-gray-200"
                    : "bg-gray-50 text-gray-400 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* ✅ Main Document Modal */}
        {isModalOpendoc && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-2xl p-2 w-[95%] lg:w-[600px] xl:w-[800px] max-h-[95vh] overflow-y-auto relative animate-fadeIn">
              {/* ❌ Close Button */}
              <button
                onClick={() => {
                  setIsModalOpenDoc(false);
                  setSelectedUserDocs([]);
                  setSelectedDocId(null);
                  setLocalComment("");
                }}
                className="absolute top-3 right-4 text-red-600 hover:text-gray-700 transition"
              >
                <FaTimes size={26} />
              </button>

              {/* 🧾 Title */}
              <h2 className="text-xl font-semibold mb-4 text-center border-b pb-2">
                Documents for{" "}
                <span className="text-blue-600">
                  {selectedUser?.name ||
                    selectedUser?.company_details?.company_name ||
                    "User"}
                </span>
              </h2>

              {selectedUserDocs.length > 0 ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {/* 📁 Left: Document Management */}
                    <div className="border rounded-lg p-2 bg-gray-50 shadow-sm">
                      <h3 className="text-base font-medium mb-2 text-gray-700">
                        Document Management
                      </h3>

                      {/* Document Selector */}
                      <label className="block text-sm mb-1 font-medium text-gray-600">
                        Select Document
                      </label>
                      <select
                        value={selectedDocId ?? ""}
                        onChange={(e) => setSelectedDocId(e.target.value)}
                        className="border rounded-md px-2 py-1 w-full mb-3 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                      >
                        <option value="">-- Choose Document --</option>
                        {selectedUserDocs.map((doc) => (
                          <option key={doc.document_id} value={doc.document_id}>
                            {getDocumentName(doc)}
                          </option>
                        ))}
                      </select>

                      <div className="flex justify-end">
                        {/*  Download Button */}
                        {selectedDocId && (
                          <button
                            onClick={handleDownloadDocument}
                            className="mt-1 mb-1 bg-blue-600 hover:bg-blue-700 text-white text-sm px-1 py-0 rounded-md shadow flex items-center gap-1"
                          >
                            <MdDownload className="text-lg" />
                            Download_
                            {getDocumentName(
                              selectedUserDocs.find(
                                (doc) => doc.document_id == selectedDocId,
                              ),
                            )}
                          </button>
                        )}
                      </div>

                      {/* 🖼️ File Preview */}
                      <DocumentPreview
                        selectedDocId={selectedDocId}
                        selectedUserDocs={selectedUserDocs}
                        handleImageClick={handleImageClick}
                        setFileUrlParent={setFileUrl}
                      />

                      {/* 🧾 Document Status Section */}
                      <DocumentStatusSection
                        selectedUser={selectedUser}
                        selectedDocId={selectedDocId}
                        setSelectedUserDocs={setSelectedUserDocs}
                        selectedUserDocs={selectedUserDocs}
                        localComment={localComment}
                        setLocalComment={setLocalComment}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        setIsModalOpenDoc={setIsModalOpenDoc}
                      />
                    </div>

                    {/* 💻 Right: Digital KYC Section */}
                    <div className="border rounded-lg p-2 bg-gray-50 shadow-sm">
                      <h3 className="text-base font-medium mb-2 text-gray-700">
                        Digital KYC
                      </h3>
                      <DigitalKycSection
                        selectedUser={selectedUser}
                        setSelectedUser={setSelectedUser}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        setIsModalOpenDoc={setIsModalOpenDoc}
                      />

                      <div className="border rounded-lg p-2 bg-gray-50 shadow-sm">
                        <h3 className="text-base font-medium mb-2 text-gray-700">
                          Physical KYC
                        </h3>
                        <PhysicalKycSection
                          selectedUser={selectedUser}
                          setSelectedUser={setSelectedUser}
                          isLoading={isLoading}
                          setIsLoading={setIsLoading}
                          setIsModalOpenDoc={setIsModalOpenDoc}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center mt-5">
                  No documents available
                </p>
              )}
            </div>
          </div>
        )}

        {/* 🖼️ Image Preview Modal */}
        {previewImage && (
          <div
            className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center"
            onClick={closePreview}
          >
            <div className="relative max-w-4xl max-h-[90vh]">
              <button
                onClick={closePreview}
                className="absolute -top-8 right-0 text-white hover:text-gray-300"
              >
                <FaTimes size={24} />
              </button>
              <img
                src={previewImage}
                alt="Full Preview"
                className="max-w-full max-h-[90vh] rounded-lg border border-gray-300 shadow-lg"
              />
            </div>
          </div>
        )}

        {/* ✅ Confirmation Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl p-3 w-[90%] max-w-md relative animate-fadeIn">
              {/* ❌ Close Button */}
              <button
                onClick={() => {
                  setIsModalOpen(false)
                  setReferredBy("")
                  handlePageChange(pagination.current_page)
                }}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
                disabled={isLoading}
              >
                <FaTimes size={18} />
              </button>

              {/* 🔹 Title */}
              <h2 className="text-lg font-semibold mb-3 text-gray-800">
                Confirm Action
              </h2>

              {/* ℹ️ Message */}
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to change the status to{" "}
                <span
                  className={`font-semibold ${
                    selectedStatus === "REJECTED"
                      ? "text-red-600"
                      : selectedStatus === "APPROVED"
                        ? "text-green-600"
                        : selectedStatus === "PENDING"
                          ? "text-blue-600"
                          : "text-yellow-600"
                  }`}
                >
                  {selectedStatus}
                </span>{" "}
                for user{" "}
                <span className="font-semibold text-gray-800">
                  {selectedUser?.businessName || selectedUser?.name}
                </span>
                ?
              </p>

              {/* referred By */}
             
              <select
                onChange={(e) => setReferredBy(e.target.value)}
                name="referred_by"
                id=""
                value={referredBy}
                className="w-full border rounded-md mb-4 p-2 text-sm focus:ring-1 focus:ring-blue-500 resize-none"
              >
                <option value="">
                  Select Employee
                </option>
                {allEmployees?.map((item, idx) => {
                  return (
                    <option value={item?.user_id}>
                      {item?.user_id} {item?.name}
                    </option>
                  );
                })}
              </select>

              {/* 💬 Comment Input */}
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment <span className="text-red-500">*</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter your comment..."
                rows={3}
                className="w-full border rounded-md p-2 text-sm focus:ring-1 focus:ring-blue-500 resize-none"
              ></textarea>

              {/* ✅ Confirm Checkbox */}
              <div className="flex items-center mt-3">
                <input
                  type="checkbox"
                  id="confirmCheck"
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="confirmCheck"
                  className="ml-2 text-sm text-gray-700 select-none"
                >
                  I confirm that I want to change this status.
                </label>
              </div>

              {/* 🚀 Action Buttons */}
              <div className="mt-5 flex justify-end space-x-2">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 text-sm rounded-md hover:bg-gray-400 transition"
                  disabled={isLoading}
                >
                  Cancel
                </button>

                <button
                  onClick={handleConfirmUpdate}
                  className={`px-4 py-2 text-sm rounded-md text-white transition ${
                    selectedStatus === "REJECTED"
                      ? "bg-red-500 hover:bg-red-600"
                      : selectedStatus === "APPROVED"
                        ? "bg-green-500 hover:bg-green-600"
                        : selectedStatus === "PENDING"
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-yellow-500 hover:bg-yellow-600"
                  } ${
                    !isConfirmed || !comment.trim()
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                  disabled={isLoading || !isConfirmed || !comment.trim()}
                >
                  {isLoading ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer />
      </DivTag>
    </motion.div>
  );
};

export default SignUpReport;

const DocumentPreview = ({
  selectedDocId,
  selectedUserDocs,
  setFileUrlParent,
}) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const doc = selectedUserDocs.find(
    (d) => String(d.document_id) === String(selectedDocId),
  );

  useEffect(() => {
    if (!doc?.document_path) return;

    const fetchDocument = async () => {
      setIsLoading(true);
      try {
        const res = await DocumentPreviewService(doc.document_path);

        const dataUri = res?.dataUri || res?.data?.dataUri;
        if (dataUri) {
          setFileUrl(dataUri);
          setFileUrlParent(dataUri);
        } else {
          showErrorToast("Unable to load document preview.");
        }
      } catch (error) {
        const errMsg =
          error?.response?.data?.status?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Error fetching document preview!";
        showErrorToast(errMsg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [doc?.document_path]);

  const handleImageClick = (url) => setPreviewImage(url);
  const closePreview = () => setPreviewImage(null);

  if (!doc) return null;
  if (isLoading)
    return <div className="text-sm text-gray-500">Loading preview...</div>;
  if (!fileUrl)
    return <div className="text-sm text-gray-500">No preview available</div>;

  //  Detect file type dynamically from Base64 data
  const isPDF = fileUrl.startsWith("data:application/pdf");
  const isImage = fileUrl.startsWith("data:image/");

  return (
    <>
      {/* 📄 Document Thumbnail */}
      <div className="mb-2">
        {isImage ? (
          <img
            src={fileUrl}
            alt="Document"
            className="w-full max-h-40 object-contain rounded border cursor-pointer hover:opacity-90 transition"
            onClick={() => handleImageClick(fileUrl)}
          />
        ) : isPDF ? (
          <iframe
            src={fileUrl}
            title="PDF Preview"
            className="w-full h-60 border rounded"
          />
        ) : (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Open File
          </a>
        )}
      </div>

      {/* 🖼️ Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center"
          onClick={closePreview}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={closePreview}
              className="absolute -top-8 right-0 text-white hover:text-gray-300"
            >
              <FaTimes size={24} />
            </button>
            <img
              src={previewImage}
              alt="Full Preview"
              className="max-w-full max-h-[90vh] rounded-lg border border-gray-300 shadow-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

const DocumentStatusSection = ({
  selectedUser,
  selectedDocId,
  selectedUserDocs,
  localComment,
  setSelectedUserDocs,
  setLocalComment,
  isLoading,
  setIsLoading,
  setIsModalOpenDoc,
}) => {
  const doc = selectedUserDocs.find(
    (d) => String(d.document_id) === String(selectedDocId),
  );
  if (!doc) return null;

  const currentStatus = doc?.status?.value?.toLowerCase() || "pending";

  const handleSubmitStatus = async (statusValue) => {
    if (!localComment.trim()) {
      showErrorToast("Please enter a comment before submitting!");
      return;
    }

    setIsLoading(true);

    try {
      const response = await UpdateDoctStatus({
        user_id: selectedUser.id,
        document_id: doc.document_id,
        status: statusValue.toUpperCase(),
        comment: localComment,
      });

      // console.log("🔥 FULL API RESPONSE:", response);

      const res = response?.data ?? response;
      const statusCode =
        res?.data?.status?.code || res?.status?.code || res?.status || 200;

      // ✅ Extract errors (can be string or object)
      let errorMessage = res?.data?.errors || res?.errors || null;

      // ✅ Convert object errors to string
      if (errorMessage && typeof errorMessage === "object") {
        errorMessage = Object.values(errorMessage).flat().join(", ");
      }

      // ✅ Handle 422 or any non-200 status with error
      if (statusCode === 422 || (statusCode !== 200 && errorMessage)) {
        const fallbackMsg =
          res?.data?.status?.message ||
          res?.status?.message ||
          "Validation failed";
        showErrorToast(errorMessage || fallbackMsg);
        return;
      }

      // ✅ Handle explicit error responses even with 200
      if (
        errorMessage &&
        (res?.data?.status?.type === "error" || res?.status?.type === "error")
      ) {
        showErrorToast(errorMessage);
        return;
      }

      // ✅ SUCCESS
      const successMessage =
        res?.data?.status?.message ||
        res?.status?.message ||
        res?.message ||
        "Action completed successfully";

      showSuccessToast(successMessage);

      const updatedDocs = selectedUserDocs.map((d) =>
        d.document_id === doc.document_id
          ? { ...d, status: { ...d.status, value: statusValue.toLowerCase() } }
          : d,
      );

      setSelectedUserDocs?.(updatedDocs);
      setIsModalOpenDoc(true);
    } catch (error) {
      const errData = error?.response?.data;

      // ✅ Handle errors (string or object)
      let err = errData?.errors || errData?.data?.errors || null;

      if (err && typeof err === "object") {
        err = Object.values(err).flat().join(", ");
      }

      // ✅ Fallback to message if no errors field
      if (!err) {
        err =
          errData?.data?.status?.message ||
          errData?.status?.message ||
          errData?.message ||
          error?.message ||
          "Request failed";
      }

      showErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white border rounded-xl shadow-md p-2 mt-1">
      {/*  Document Status */}
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Document Status</h3>
        <span
          className={`text-xs px-2 py-1 rounded-full capitalize ${
            currentStatus === "approved"
              ? "bg-green-100 text-green-700"
              : currentStatus === "rejected"
                ? "bg-red-100 text-red-700"
                : "bg-yellow-100 text-yellow-700"
          }`}
        >
          {currentStatus}
        </span>
      </div>

      {/*  Comment Section */}
      {currentStatus === "pending" && (
        <textarea
          value={localComment}
          onChange={(e) => setLocalComment(e.target.value)}
          placeholder="Enter comment..."
          className="w-full border rounded-md p-2 text-sm mb-2 focus:ring focus:ring-blue-200"
          rows={3}
        ></textarea>
      )}

      {/*  Action Buttons or Status Text */}
      {currentStatus === "approved" ? (
        <div className="text-center text-green-600 text-sm font-semibold">
          ✅ Document Approved
        </div>
      ) : currentStatus === "rejected" ? (
        <div className="text-center text-red-600 text-sm font-semibold">
          ❌ Document Rejected
        </div>
      ) : (
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => handleSubmitStatus("approved")}
            disabled={isLoading}
            className="w-1/2 text-xs px-2 py-1 rounded text-white bg-green-500 hover:bg-green-600 disabled:opacity-50"
          >
            {isLoading ? "Processing..." : "Approve"}
          </button>
          <button
            onClick={() => handleSubmitStatus("rejected")}
            disabled={isLoading}
            className="w-1/2 text-xs px-2 py-1 rounded text-white bg-red-500 hover:bg-red-600 disabled:opacity-50"
          >
            {isLoading ? "Processing..." : "Reject"}
          </button>
        </div>
      )}
    </div>
  );
};

const PhysicalKycSection = ({
  selectedUser,
  setSelectedUser,
  isLoading,
  setIsLoading,
  setIsModalOpenDoc,
}) => {
  const [remarks, setRemarks] = useState("");
  const [statusValue, setStatusValue] = useState(selectedUser?.kyc_status_id);

  // 🔄 Sync with parent when selectedUser changes
  useEffect(() => {
    if (selectedUser?.kyc_status_id) {
      setStatusValue(selectedUser.kyc_status_id);
    }
  }, [selectedUser]);

  useEffect(() => {
    console.log("selected user", selectedUser);
    console.log("status value", statusValue);
  }, [selectedUser, statusValue]);
  // ===========================
  //   HANDLE PHYSICAL KYC
  // ===========================
  const handlePhysical = async (action) => {
    if (!remarks.trim())
      return showErrorToast("Please enter a remark before submitting!");

    setIsLoading(true);

    try {
      const res = await UserKycStatus({
        user_id: selectedUser?.user_id,
        status_id: action, // approve or reject
        remarks,
      });

      // 🔍 Extract safely
      const code =
        res?.status?.code || res?.data?.status?.code || res?.code || 500;

      const serverSuccessMsg =
        res?.status?.message ||
        res?.data?.status?.message ||
        res?.message ||
        "Updated successfully";

      let serverErrorMsg =
        res?.errors ||
        res?.data?.errors ||
        res?.status?.message ||
        res?.data?.status?.message ||
        res?.message ||
        "Validation error";

      // Normalize error to string
      if (typeof serverErrorMsg === "object") {
        serverErrorMsg = Array.isArray(serverErrorMsg)
          ? serverErrorMsg.join(", ")
          : Object.values(serverErrorMsg).flat().join(", ");
      }

      if (res?.status?.code === 200 || res?.data?.status?.code === 200) {
        // ✅ Success
        showSuccessToast(serverSuccessMsg);

        // Instantly update UI
        setStatusValue(res?.data?.kyc_status_id);

        if (typeof setSelectedUser === "function") {
          setSelectedUser((prev) => ({
            ...prev,
            kyc_status_id: res?.data?.kyc_status_id,
          }));
        }

        setIsModalOpenDoc(true);
      }

      // ❌ API FAILED
      if (code !== 200) {
        showErrorToast(serverErrorMsg);
        return;
      }
    } catch (error) {
      // ========= CATCH 422 / NETWORK / OTHER ERRORS =========
      let err =
        error?.response?.data?.errors ||
        error?.response?.data?.status?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Request failed";

      if (typeof err === "object") {
        err = Array.isArray(err)
          ? err.join(", ")
          : Object.values(err).flat().join(", ");
      }

      showErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-3 mt-2 border border-gray-700">
      <h2 className="text-base font-semibold text-gray-700 mb-3 border-b pb-2">
        Physical KYC Verification
      </h2>

      {/* Remarks only when pending or REKYC */}
      {(statusValue !== 2 || statusValue === "rekyc") && (
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter remarks..."
          className="w-full border rounded-md p-2 text-sm mb-3 focus:ring focus:ring-yellow-200"
          rows={3}
        ></textarea>
      )}

      {/* STATUS VIEW */}
      <div className="flex justify-center items-center gap-4">
        {statusValue === "approved" ||
        statusValue === "approve" ||
        statusValue === 2 ? (
          <span className="text-green-600 font-semibold text-sm">
            ✅ Approved
          </span>
        ) : (
          <>
            {/* Approve Button */}
            <button
              onClick={() => handlePhysical("approve")}
              disabled={isLoading}
              className="w-1/3 text-xs px-2 py-1 rounded text-white bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50"
            >
              {isLoading ? "Processing..." : "Approve Physical"}
            </button>

            {/* Reject Button */}
            <button
              onClick={() => handlePhysical("reject")}
              disabled={isLoading || statusValue === 3}
              className={`w-1/3 text-xs px-2 py-1 rounded text-white ${statusValue === 3 ? "bg-red-500 hover:bg-red-600" : "bg-orange-500 hover:bg-orange-600"}  disabled:opacity-70`}
            >
              {isLoading
                ? "Processing..."
                : statusValue === 3
                  ? "Rejected"
                  : "Reject Physical"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const DigitalKycSection = ({
  selectedUser,
  setSelectedUser,
  isLoading,
  setIsLoading,
  setIsModalOpenDoc,
}) => {
  const [remarks, setRemarks] = useState({
    aadhar: "",
    pan: "",
    mobile: "",
    email: "",
  });

  const isVerified = (field) => {
    const verifiedKey = `${field}_verified_at`;
    return !!selectedUser?.[verifiedKey];
  };

  const handleDigital = async (field, statusValue) => {
    if (!remarks[field]?.trim()) {
      return showErrorToast("Please enter a comment!");
    }

    setIsLoading(true);

    try {
      const response = await UserDigitalKycStatus({
        user_id: selectedUser?.id,
        field,
        remarks: remarks[field],
        status_id: statusValue === "approved" ? "approve" : "reject",
      });

      const res = response?.data ?? response;
      const code = res?.status?.code ?? 500;

      // Extract structured errors
      let errorMessage = null;
      if (res?.errors && typeof res.errors === "object") {
        errorMessage = Object.values(res.errors).flat().join(", ");
      }

      if (
        response?.status?.code === 200 ||
        response?.data?.status?.code === 200
      ) {
        return showSuccessToast(response?.message || response?.data?.message);
      }

      // 🔥 SPECIAL CASE — Aadhar already verified (422)
      if (code === 422) {
        const msg =
          res?.status?.message || res?.message || "Aadhar already verified";
        showErrorToast(msg);
        return;
      }

      // ❌ ANY OTHER FAILURE
      if (code !== 200) {
        const fallbackMsg =
          errorMessage ||
          res?.status?.message ||
          res?.message ||
          "Validation failed!";
        showErrorToast(fallbackMsg);
        return;
      }

      // ✅ SUCCESS (200)
      const successMessage =
        res?.status?.message ||
        res?.message ||
        `${field.toUpperCase()} KYC ${statusValue} successfully!`;

      showSuccessToast(successMessage);

      // Update UI immediately
      if (statusValue === "approved") {
        setSelectedUser((prev) => ({
          ...prev,
          [`${field}_verified_at`]: new Date().toISOString(),
        }));
      }

      // Clear remark after success
      setRemarks((prev) => ({
        ...prev,
        [field]: "",
      }));

      setIsModalOpenDoc(true);
    } catch (error) {
      const errData = error?.response?.data;

      let finalError = null;

      if (errData?.errors && typeof errData.errors === "object") {
        finalError = Object.values(errData.errors).flat().join(", ");
      }

      if (!finalError) {
        finalError =
          errData?.status?.message ||
          errData?.message ||
          error?.message ||
          "Error updating Digital KYC!";
      }

      showErrorToast(finalError);
    } finally {
      setIsLoading(false);
    }
  };

  const kycFields = [
    { label: "Aadhar Verification", field: "aadhar" },
    { label: "PAN Verification", field: "pan" },
    { label: "Mobile Verification", field: "mobile" },
    { label: "Email Verification", field: "email" },
  ];

  return (
    <div className="border border-gray-500 rounded-md mt-3">
      <h3 className="text-center">Digital KYC</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
        {kycFields.map(({ label, field }) => {
          const verified = isVerified(field);

          return (
            <div
              key={field}
              className={`bg-white border rounded-xl shadow-md p-2 flex flex-col justify-between ${
                verified ? "border-green-400" : "border-gray-300"
              }`}
            >
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex justify-between items-center">
                {label}
                {verified && (
                  <span className="text-green-600 text-xs font-medium">
                    ✅ Verified
                  </span>
                )}
              </h3>

              {!verified && (
                <>
                  <textarea
                    value={remarks[field]}
                    onChange={(e) =>
                      setRemarks({ ...remarks, [field]: e.target.value })
                    }
                    placeholder="Enter comment"
                    rows={2}
                    className="border rounded-md w-full text-sm p-1 mb-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDigital(field, "approved")}
                      disabled={isLoading}
                      className="w-1/2 text-xs px-2 py-1 rounded text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
                    >
                      {isLoading ? "Processing..." : "Approve"}
                    </button>

                    <button
                      onClick={() => handleDigital(field, "rejected")}
                      disabled={isLoading}
                      className="w-1/2 text-xs px-2 py-1 rounded text-white bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50"
                    >
                      {isLoading ? "Processing..." : "Reject"}
                    </button>
                  </div>
                </>
              )}

              {verified && (
                <p className="text-xs text-gray-500 mt-1">
                  Verified on:{" "}
                  <span className="text-gray-700 font-medium">
                    {selectedUser?.[`${field}_verified_at`]
                      ? new Date(
                          selectedUser[`${field}_verified_at`],
                        ).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "N/A"}
                  </span>
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
